import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Wrench,
  ShieldCheck,
  Gauge,
  Flame,
  Settings,
  Target,
  CheckCircle2,
  AlertTriangle,
  Microscope,
  Hammer,
  Package,
  TestTube,
  Clock,
  Award,
  Droplets,
  Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const ACCENT = "#0062A8"; // Ford classic blue
const ACCENT_HOT = "#E8372C"; // alert/attention red

const ENGINES = [
  {
    code: "1.0",
    label: "1.0 EcoBoost",
    power: "100 — 155 LE",
    models: "Fiesta · Focus · EcoSport · Puma · C-Max · Mondeo",
    issue:
      "Hűtővíz-szivárgás a hengerfejbe, hengerfal-repedés, dugattyú- és csapágyproblémák. Perselyezéssel megmenthető a motor.",
    testId: "engine-1-0",
  },
  {
    code: "1.5",
    label: "1.5 EcoBoost",
    power: "150 — 182 LE",
    models: "Focus · Kuga · Mondeo · Edge · Escape",
    issue:
      "Hűtővíz-szivárgás a hengerfejtömítésen át, kompresszió-vesztés, hengerfal-repedés. Idejében elkapva perselyezéssel orvosolható.",
    testId: "engine-1-5",
  },
  {
    code: "1.6",
    label: "1.6 EcoBoost",
    power: "150 — 182 LE",
    models: "Focus ST · Fiesta ST · Kuga · Mondeo · S-Max · Galaxy",
    issue:
      "Hengerfej-tömítés hibái, hűtővíz-szivárgás, hengerfal-repedés, motortűz-visszahívások. Perselyezés gyakori megoldás.",
    testId: "engine-1-6",
  },
  {
    code: "2.0",
    label: "2.0 EcoBoost",
    power: "203 — 253 LE",
    models: "Mondeo · Edge · Explorer · Kuga ST · S-Max · Galaxy · Lincoln MKC",
    issue:
      "Hengerfal-repedés, hűtővíz-szivárgás a hengerfejből, vezérműlánc és PCV szelep problémák. Tuningolt motoroknál dugattyú-károsodás.",
    testId: "engine-2-0",
  },
  {
    code: "2.3",
    label: "2.3 EcoBoost",
    power: "280 — 350 LE",
    models: "Focus RS · Mustang · Ranger Raptor · Explorer ST · Lincoln",
    issue:
      "Nagyobb teljesítményű turbós motor — hengerfal-repedés, hengerfej-tömítés probléma, dugattyú-károsodás tuning után.",
    testId: "engine-2-3",
  },
];

const WHY_SLEEVING = [
  {
    icon: Target,
    title: "Gyári tolerancián belül",
    text:
      "A szakszerű perselyezés során a hengerfurat pontosabb lesz, mint az eredeti alumínium felület — mikrométer alatti pontossággal.",
  },
  {
    icon: ShieldCheck,
    title: "Erősebb anyag, mint a gyári",
    text:
      "Edzett acél / lemezgrafitos öntöttvas persely — hőtágulása és kopásállósága felülmúlja a gyári alumíniumot. Hosszabb élettartam.",
  },
  {
    icon: Award,
    title: "Garancia a munkára",
    text:
      "Minden perselyezett blokkra írásbeli garancia — a kivitelezésre és az anyagra egyaránt. A javítás tényleg tart, nem csak papíron.",
  },
  {
    icon: Gauge,
    title: "Visszatér a gyári teljesítmény",
    text:
      "A perselyezett motor pontosan a gyári LE és nyomaték adatokkal fut — stabil kompresszió, nincs olajfogyás, nincs rezgés.",
  },
];

const PROCESS = [
  {
    n: "01",
    icon: Microscope,
    title: "Diagnosztika — Ford IDS",
    text:
      "A motor hibajelenségeinek pontos feltérképezése márka-diagnosztikával (Ford IDS/Mahindra), kompresszió-mérés, endoszkópos vizsgálat.",
  },
  {
    n: "02",
    icon: Settings,
    title: "Bontás + mérés",
    text:
      "Motor szétszerelése műhelyben, blokk és hengerfej külön. Mikrométerrel pontos állapotfelmérés — mi javítható, mi cserélendő.",
  },
  {
    n: "03",
    icon: Hammer,
    title: "Blokkperselyezés",
    text:
      "Hengerfurat precíziós fúrása, acél vagy öntöttvas persely melegen préselt beillesztése. Honolás a végleges méretre — tükörsima felület.",
  },
  {
    n: "04",
    icon: Package,
    title: "Új alkatrészek + szerelés",
    text:
      "Új dugattyúk, csapágyak, tömítések, olajpumpa. Nyomatékkulcsos szerelés gyári specifikáció szerint.",
  },
  {
    n: "05",
    icon: TestTube,
    title: "Beüzemelés + próbaút",
    text:
      "Olajjal, hűtőfolyadékkal feltöltés, első indítás, alapjárat betartás, próbaút terhelve. Ismételt mérések, hibakódok reset.",
  },
  {
    n: "06",
    icon: CheckCircle2,
    title: "Átadás + garancia",
    text:
      "Dokumentált munkalap, használt alkatrészek átadása, írásbeli garancia a perselyezésre és a munka minőségére.",
  },
];

const SYMPTOMS = [
  {
    icon: Flame,
    title: "Hideg indításnál fehér füst",
    text:
      "Reggeli indulásnál sűrű, fehér gőz jön a kipufogóból — hűtőfolyadék kerül az égéstérbe. A legfontosabb korai jele a blokkrepedésnek.",
  },
  {
    icon: Zap,
    title: "Gyújtáskimaradás, motorhibajel",
    text:
      "Rángat, lefullad, rázós alapjárat, kigyúl a check-engine lámpa (misfire kódokkal). Hűtővíz az égéstérben → gyertya nem gyújt rendesen.",
  },
  {
    icon: Droplets,
    title: "Hűtővízfogyasztás",
    text:
      "Fogy a tágulási tartály szintje, de látható szivárgás a motortérben nincs. A víz a hengerbe szivárog, és a kipufogón át elpárolog.",
  },
  {
    icon: Wrench,
    title: "Kompresszió-vesztés",
    text:
      "Nem húz, nehezen indul, rángat — hengerenként eltérő kompresszió. Perselyezéssel visszaállítható a gyári érték.",
  },
];

const FAQ = [
  {
    q: "Miért éri meg perselyezni egy új motor helyett?",
    a: "Egy új 1.0/1.5/2.0 EcoBoost motor hazai ára 1,2 — 2,5 millió Ft között mozog alkatrészként, plusz a beépítés. Egy szakszerű perselyezés + motorfelújítás ennek töredéke, és a kapott eredmény gyárinál jobb (erősebb anyag, pontosabb tűrés). Az autó értékállóságát is jobban megőrzi.",
  },
  {
    q: "Mire vonatkozik a garancia?",
    a: "A perselyezés anyagára és a munka kivitelezésére egyaránt — írásban. Pontos feltételek a munkalap részét képezik. A nem perselyezett alkatrészekre (pl. szenzorok, befecskendezők) külön garancia vonatkozik.",
  },
  {
    q: "Minden EcoBoost motor javítható?",
    a: "A legtöbb igen — kivéve, ha a főcsapágyház repedt vagy a blokk alumíniuma súlyosan sérült. Diagnosztika után mondjuk meg biztosan. A diagnosztika érdemi, ingyenes árajánlat része.",
  },
  {
    q: "Mennyi idő egy teljes motorfelújítás?",
    a: "A folyamat jellemzően 2 — 4 hét, mert a perselyezés precíz, több napos munka, és utána összeszerelés + próbaút. Pontos határidőt a diagnosztikai eredmény után tudunk mondani.",
  },
  {
    q: "Mi a különbség a 'cseremotor' és a perselyezett motor között?",
    a: "A cseremotor egy másik, használt motor — nem tudod, meddig fog kibírni, és ugyanaz a probléma újra előjöhet. A perselyezett motor a te saját motorod, új, erősebb anyagokkal felújítva, írásbeli garanciával. Hosszabb távon megbízhatóbb választás.",
  },
  {
    q: "Mennyibe kerül egy perselyezés?",
    a: "A motor típusától, állapotától és a cserélendő alkatrészek számától függ. Diagnosztika után részletes árajánlatot adunk — a tényleges árról tájékoztatunk, mielőtt bármit elkezdenénk. Hívjon vagy küldjön üzenetet.",
  },
];

export default function FordEcoboostPage() {
  useDocumentMeta({
    title:
      "Ford EcoBoost motorfelújítás Debrecen — Blokkperselyezés garanciával | Autó-Szerelem",
    description:
      "Ford 1.0 / 1.5 / 1.6 / 2.0 / 2.3 EcoBoost motorok teljes körű felújítása Debrecenben. Blokkperselyezés gyárinál jobb technológiával, írásbeli garanciával. Hűtővíz-szivárgás, hengerfal-repedés, hengerfej-tömítés javítása. +36 30 863 7476",
    path: "/ford-ecoboost-felujitas",
  });

  React.useEffect(() => {
    const id = "ford-ecoboost-jsonld";
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Ford EcoBoost motorfelújítás",
        name: "Ford EcoBoost blokkperselyezés és teljes motorfelújítás",
        description:
          "Ford 1.0, 1.5, 1.6, 2.0 és 2.3 EcoBoost benzinmotorok szakszerű felújítása — blokkperselyezés gyárinál jobb technológiával, írásbeli garanciával.",
        provider: {
          "@type": "AutoRepair",
          name: "Autó-Szerelem — Séra Autószervíz",
          telephone: "+36-30-863-7476",
          email: "autoszerelem@gmail.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Vadvirág utca 15/D — 3. kapu",
            addressLocality: "Debrecen",
            postalCode: "4031",
            addressCountry: "HU",
          },
        },
        areaServed: { "@type": "City", name: "Debrecen" },
        url: "https://autoszerelem.hu/ford-ecoboost-felujitas",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ]);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="ford-ecoboost-page">
      <Navbar />

      {/* HERO */}
      <section
        className="relative pt-24 sm:pt-28 isolate overflow-hidden"
        data-testid="ford-hero-section"
      >
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute top-10 left-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.10]"
            style={{ background: `radial-gradient(circle, ${ACCENT} 0%, transparent 70%)` }}
          />
          <div
            className="absolute bottom-0 right-10 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.06]"
            style={{ background: `radial-gradient(circle, ${ACCENT_HOT} 0%, transparent 70%)` }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 sm:py-28">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors mb-10"
            data-testid="ford-back-link"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} />
            Vissza a főoldalra
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <span className="section-eyebrow mb-4">Ford · EcoBoost · Motorfelújítás</span>

              <h1
                className="mt-6 font-black uppercase tracking-tight text-white leading-[0.9]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2rem, 7vw, 6rem)",
                }}
                data-testid="ford-hero-headline"
              >
                Ford EcoBoost
                <br />
                <span style={{ color: ACCENT }}>blokkperselyezés</span>
                <br />
                garanciával
              </h1>

              <p className="mt-8 max-w-2xl text-zinc-300 text-base sm:text-lg leading-relaxed">
                Nem új motor. Nem cseremotor. <strong className="text-white">A sajátja</strong>,
                szakszerűen felújítva — <strong className="text-white">gyárinál jobb
                technológiával</strong>, írásbeli garanciával.
              </p>

              <p className="mt-4 max-w-2xl text-zinc-400 leading-relaxed">
                Specializáltunk a Ford EcoBoost benzinmotorok teljes körű felújítására:
                <strong className="text-white"> 1.0, 1.5, 1.6, 2.0 és 2.3 EcoBoost</strong>. Hengerfal-repedés,
                hűtővíz-szivárgás, hengerfej-tömítés probléma — ha a Ford márkaszerviz új motort
                ajánlott, érdemes először minket megkérdezni.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/#foglalas"
                  className="btn-primary inline-flex items-center"
                  data-testid="ford-hero-cta-booking"
                >
                  Diagnosztikai időpont
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <a
                  href="tel:+36308637476"
                  className="btn-ghost inline-flex items-center"
                  data-testid="ford-hero-cta-call"
                >
                  <Phone className="w-4 h-4 mr-2" strokeWidth={2} />
                  +36 30 863 7476
                </a>
              </div>
            </div>

            {/* RIGHT — Hero badge: ALL EcoBoost */}
            <div className="lg:col-span-5">
              <div
                className="relative bg-[#0d0d0d] border border-white/10 p-8 overflow-hidden"
                data-testid="ford-hero-engines"
              >
                <div className="m-stripe absolute top-0 left-0 right-0 h-[2px]" />

                <div
                  className="inline-flex items-center gap-2 text-[0.65rem] font-bold uppercase tracking-[0.2em] mb-6"
                  style={{ color: ACCENT, fontFamily: "Barlow Condensed" }}
                >
                  <Flame className="w-3.5 h-3.5" strokeWidth={2} />
                  Minden benzines EcoBoost
                </div>

                <h3
                  className="font-black uppercase tracking-tight text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(1.5rem, 3vw, 2.25rem)",
                  }}
                >
                  1.0 · 1.5 · 1.6 · 2.0 · 2.3
                </h3>

                <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                  Fiesta, Focus, Kuga, Mondeo, EcoSport, Puma, S-Max, Edge, Explorer,
                  Mustang, Ranger Raptor és minden egyéb Ford EcoBoost benzinmotoros modell.
                </p>

                <div className="mt-6 space-y-3">
                  {[
                    "Gyári tolerancián belüli honolás",
                    "Edzett acél / öntöttvas persely",
                    "Írásbeli garancia a kivitelezésre",
                    "Dokumentált, mikrométeres mérések",
                  ].map((b) => (
                    <div key={b} className="flex items-start gap-3 text-sm text-zinc-300">
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: ACCENT }} strokeWidth={2} />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ENGINES — Supported */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="ford-engines-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Támogatott motorok</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
                data-testid="ford-engines-headline"
              >
                EcoBoost típusok,
                <br />
                <span style={{ color: ACCENT }}>amiket felújítunk</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              Minden benzines EcoBoost motor típusnak megvan a saját
              hibatípusa. Alább a leggyakoribb modell-específikus problémák, amiket
              rutinból kezelünk.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {ENGINES.map((e) => (
              <div
                key={e.code}
                className="relative bg-[#0d0d0d] border border-white/10 p-6 hover:border-white/20 transition-colors"
                data-testid={e.testId}
              >
                <div
                  className="absolute top-0 left-0 h-1 w-full"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}, transparent)` }}
                />
                <div
                  className="font-black leading-none text-white mb-3"
                  style={{ fontFamily: "Barlow Condensed", fontSize: "3.25rem" }}
                >
                  {e.code}
                </div>
                <div
                  className="text-[0.65rem] font-bold uppercase tracking-[0.2em] mb-5"
                  style={{ color: ACCENT, fontFamily: "Barlow Condensed" }}
                >
                  {e.label} · {e.power}
                </div>
                <p className="text-xs text-zinc-500 leading-relaxed mb-4">
                  <strong className="text-zinc-400">Modellek:</strong> {e.models}
                </p>
                <p className="text-sm text-zinc-300 leading-relaxed">{e.issue}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SLEEVING — Why better than factory */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="ford-why-section"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div
            className="absolute top-10 right-1/3 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.06]"
            style={{ background: `radial-gradient(circle, ${ACCENT} 0%, transparent 70%)` }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <span className="section-eyebrow mb-4">Miért jobb, mint a gyári?</span>
            <h2
              className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
              }}
              data-testid="ford-why-headline"
            >
              Perselyezett blokk —
              <br />
              <span style={{ color: ACCENT }}>stabilabb, erősebb</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              A Ford EcoBoost motorok eredeti alumínium blokkja könnyű, de kopásra
              érzékeny. A szakszerű perselyezés során acél vagy öntöttvas betét kerül
              a hengerfurat helyére — hőtágulása és kopásállósága felülmúlja a gyári
              alumínium felületet.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_SLEEVING.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="bg-[#0d0d0d] border border-white/10 p-6"
                data-testid={`ford-why-${title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/gi, "")}`}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center mb-5"
                  style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: ACCENT }} strokeWidth={1.6} />
                </div>
                <h3 className="text-white font-bold mb-2">{title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE — Finished refurbished block (real workshop photo) */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="ford-showcase-section"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div
            className="absolute top-10 left-1/4 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.07]"
            style={{ background: `radial-gradient(circle, ${ACCENT} 0%, transparent 70%)` }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4">Elkészült munka</span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
                data-testid="ford-showcase-headline"
              >
                A részletek
                <br />
                <span style={{ color: ACCENT }}>beszélnek önmagukért</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              Így néz ki egy általunk felújított EcoBoost rövidblokk — hengerfej
              nélkül, 4 új perselyezett furattal, STD méretű gyári dugattyúkkal.
              Minden tükörsima, minden a helyén.
            </p>
          </div>

          {/* Image card */}
          <div
            className="relative bg-[#0d0d0d] border border-white/10 overflow-hidden shadow-2xl"
            data-testid="ford-showcase-image-wrap"
          >
            <div className="m-stripe absolute top-0 left-0 right-0 h-[2px] z-10" />

            <div className="relative">
              <img
                src="/images/ford-ecoboost-perselyezett-blokk.webp"
                alt="Felújított Ford EcoBoost rövidblokk — 4 perselyezett henger, STD dugattyúkkal, hengerfej nélkül"
                className="w-full h-auto block"
                loading="lazy"
                width="2000"
                height="834"
                data-testid="ford-showcase-image"
              />

              {/* Top-left label overlay */}
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-white/10 px-3 py-1.5 pointer-events-none">
                <span
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ background: ACCENT }}
                  aria-hidden="true"
                />
                <span
                  className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-white"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Felújított EcoBoost blokk · 4 persely
                </span>
              </div>

              {/* Bottom-right watermark */}
              <div className="absolute bottom-5 right-5 bg-black/70 backdrop-blur-sm border border-white/10 px-3 py-1.5 pointer-events-none">
                <span
                  className="text-[0.6rem] font-bold uppercase tracking-[0.22em] text-zinc-400"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  autoszerelem.hu · Debrecen
                </span>
              </div>
            </div>
          </div>

          {/* 3 highlight stats under the image */}
          <div className="mt-6 grid sm:grid-cols-3 gap-4" data-testid="ford-showcase-stats">
            {[
              { label: "Hengerek", value: "4", unit: "× új persely" },
              { label: "Tolerancia", value: "<0.01", unit: "mm · mikrométer" },
              { label: "Dugattyúk", value: "STD", unit: "gyári méret" },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-[#0d0d0d] border border-white/10 p-5"
                data-testid={`ford-showcase-stat-${s.label.toLowerCase()}`}
              >
                <div
                  className="text-[0.6rem] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-2"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {s.label}
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-black leading-none"
                    style={{
                      fontFamily: "Barlow Condensed",
                      color: ACCENT,
                      fontSize: "2.5rem",
                    }}
                  >
                    {s.value}
                  </span>
                  <span className="text-xs text-zinc-500">{s.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SYMPTOMS — When to come */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="ford-symptoms-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="section-eyebrow mb-4" style={{ color: ACCENT_HOT }}>
                Ezekkel jönnek
              </span>
              <h2
                className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
                style={{
                  fontFamily: "Barlow Condensed",
                  fontSize: "clamp(2.25rem, 5vw, 4rem)",
                }}
                data-testid="ford-symptoms-headline"
              >
                Tipikus tünetek,
                <br />
                <span style={{ color: ACCENT_HOT }}>ne hagyja figyelmen kívül</span>
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 leading-relaxed">
              Minél korábban diagnosztizáljuk, annál kisebb a kár és a költség. Ha
              a tünetek bármelyike megjelenik, azonnal álljon meg és hívjon.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SYMPTOMS.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="bg-[#0d0d0d] border border-white/10 p-6 hover:border-white/20 transition-colors"
                data-testid={`ford-symptom-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center mb-5"
                  style={{ background: `${ACCENT_HOT}15`, border: `1px solid ${ACCENT_HOT}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: ACCENT_HOT }} strokeWidth={1.6} />
                </div>
                <h3 className="text-white font-bold mb-2">{title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>

          {/* Diagnostic callout — the triad that specifically indicates block cracking */}
          <div
            className="mt-10 relative bg-[#0d0d0d] border p-7 sm:p-9 overflow-hidden"
            style={{ borderColor: `${ACCENT_HOT}55` }}
            data-testid="ford-block-crack-callout"
          >
            <div
              className="absolute top-0 left-0 w-full h-1"
              style={{ background: `linear-gradient(90deg, ${ACCENT_HOT}, transparent 70%)` }}
            />
            <div className="flex items-start gap-5">
              <div
                className="shrink-0 w-12 h-12 flex items-center justify-center"
                style={{ background: `${ACCENT_HOT}18`, border: `1px solid ${ACCENT_HOT}55` }}
                aria-hidden="true"
              >
                <AlertTriangle
                  className="w-6 h-6"
                  style={{ color: ACCENT_HOT }}
                  strokeWidth={1.8}
                />
              </div>
              <div className="flex-1">
                <div
                  className="text-[0.65rem] font-bold uppercase tracking-[0.22em] mb-2"
                  style={{ color: ACCENT_HOT, fontFamily: "Barlow Condensed" }}
                >
                  Figyelmeztetés
                </div>
                <h3
                  className="font-black uppercase tracking-tight text-white leading-[0.95]"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(1.5rem, 2.5vw, 2.25rem)",
                  }}
                >
                  Ha a <span style={{ color: ACCENT_HOT }}>fehér füst · gyújtáskimaradás · hűtővízfogyás</span>
                  <br />
                  együtt jelenik meg → ez blokkrepedés jele
                </h3>
                <p className="mt-4 text-zinc-300 leading-relaxed max-w-3xl">
                  Önmagában bármelyik tünet lehet kisebb hiba is, de ha mind a három
                  <strong className="text-white"> egyszerre</strong> jelentkezik, az
                  gyakorlatilag biztosan hengerfal-repedés az EcoBoost motorban. Minél tovább
                  jár ilyen állapotban, annál több járulékos kár keletkezik (dugattyú,
                  csapágyak, katalizátor). <strong className="text-white">Azonnal állítsa le
                  és hívjon.</strong>
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a
                    href="tel:+36308637476"
                    className="btn-primary inline-flex items-center"
                    data-testid="ford-crack-callout-call"
                    style={{ background: ACCENT_HOT, borderColor: ACCENT_HOT }}
                  >
                    <Phone className="w-4 h-4 mr-2" strokeWidth={2} />
                    Azonnali hívás: +36 30 863 7476
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="ford-process-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <span className="section-eyebrow mb-4">A folyamat</span>
            <h2
              className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
              }}
              data-testid="ford-process-headline"
            >
              Diagnosztika → átadás,
              <br />
              <span style={{ color: ACCENT }}>minden lépésről tájékoztatva</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROCESS.map(({ n, icon: Icon, title, text }) => (
              <div
                key={n}
                className="relative bg-[#0d0d0d] border border-white/10 p-7"
                data-testid={`ford-process-step-${n}`}
              >
                <div
                  className="absolute top-5 right-5 font-black leading-none opacity-[0.08] text-white"
                  style={{ fontFamily: "Barlow Condensed", fontSize: "5rem" }}
                >
                  {n}
                </div>
                <div
                  className="w-10 h-10 flex items-center justify-center mb-5"
                  style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}40` }}
                >
                  <Icon className="w-5 h-5" style={{ color: ACCENT }} strokeWidth={1.6} />
                </div>
                <h3 className="text-white font-bold mb-2">{title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="ford-faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <span className="section-eyebrow mb-4">Gyakori kérdések</span>
          <h2
            className="mt-5 mb-14 font-black uppercase tracking-tight text-white leading-[0.95]"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
            }}
            data-testid="ford-faq-headline"
          >
            Mielőtt felhív minket
          </h2>

          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details
                key={i}
                className="group bg-[#0d0d0d] border border-white/10 open:border-white/20 transition-colors"
                data-testid={`ford-faq-item-${i}`}
              >
                <summary className="list-none cursor-pointer p-6 flex items-start justify-between gap-4">
                  <span className="text-white font-semibold">{f.q}</span>
                  <ArrowRight
                    className="w-5 h-5 shrink-0 text-zinc-500 group-open:rotate-90 transition-transform mt-0.5"
                    strokeWidth={1.6}
                  />
                </summary>
                <div className="px-6 pb-6 text-zinc-400 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative py-24 sm:py-32 border-t border-white/5"
        data-testid="ford-cta-section"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              background: `radial-gradient(circle at 30% 50%, ${ACCENT}, transparent 60%)`,
            }}
          />
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2
            className="font-black uppercase tracking-tight text-white leading-[0.95]"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.5rem, 6vw, 5rem)",
            }}
            data-testid="ford-cta-headline"
          >
            Ha új motort javasoltak,
            <br />
            <span style={{ color: ACCENT }}>beszéljünk előbb</span>
          </h2>

          <p className="mt-6 max-w-2xl mx-auto text-zinc-300 text-base sm:text-lg leading-relaxed">
            Hozza be az autót diagnosztikára, mielőtt bármilyen döntést hoz. Pontosan
            megmondjuk, mi javítható és mennyibe kerül — garancia nélküli
            „olcsóbb megoldások" nélkül.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/#foglalas"
              className="btn-primary inline-flex items-center"
              data-testid="ford-cta-booking"
            >
              Időpontfoglalás
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <a
              href="tel:+36308637476"
              className="btn-ghost inline-flex items-center"
              data-testid="ford-cta-call"
            >
              <Phone className="w-4 h-4 mr-2" strokeWidth={2} />
              +36 30 863 7476
            </a>
          </div>

          <div className="mt-14 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { icon: Phone, label: "Telefon", value: "+36 30 863 7476", href: "tel:+36308637476" },
              { icon: Mail, label: "E-mail", value: "autoszerelem@gmail.com", href: "mailto:autoszerelem@gmail.com" },
              { icon: MapPin, label: "Cím", value: "4031 Debrecen, Vadvirág u. 15/D" },
            ].map(({ icon: Icon, label, value, href }) => {
              const content = (
                <>
                  <Icon className="w-5 h-5 mb-3" style={{ color: ACCENT }} strokeWidth={1.6} />
                  <div
                    className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    {label}
                  </div>
                  <div className="text-white text-sm">{value}</div>
                </>
              );
              return href ? (
                <a
                  key={label}
                  href={href}
                  className="bg-[#0d0d0d] border border-white/10 p-5 text-left hover:border-white/20 transition-colors"
                  data-testid={`ford-cta-tile-${label.toLowerCase()}`}
                >
                  {content}
                </a>
              ) : (
                <div
                  key={label}
                  className="bg-[#0d0d0d] border border-white/10 p-5 text-left"
                  data-testid={`ford-cta-tile-${label.toLowerCase()}`}
                >
                  {content}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
